import { useTheme } from '@emotion/react';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import { FC } from 'react';
import { H2 } from '../../../../common/Headings/Headings.styles';
import Button from '../../../../components/Button/Button';
import { Variant } from '../../../../components/Button/Button.types';
import Table from '../../../../components/Table/Table';
import { showToast } from '../../../../components/Toast/Toast';
import { genericErrorShowToastOptions } from '../../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { Buttons, StyledReviewChanges, TableRowWrapper, TableWrapper } from './ReviewChanges.styles';
import { ReviewChangesProps } from './ReviewChanges.types';
import { ValueFormatterParams } from 'ag-grid-community';

const ReviewChanges: FC<ReviewChangesProps> = ({
  currentRawProduct,
  desiredRawProduct,
  selectedRecipes,
  mutate,
  setStep,
}) => {
  const theme = useTheme();
  const editCurrentRawProduct = () => setStep(0);
  const editDesiredRawProduct = () => setStep(0);
  const editRecipes = () => setStep(1);

  const affectedRecipes = selectedRecipes?.map((recipe) => {
    return {
      specNo: recipe.specNo,
      specDescription: recipe.description
    };
  });

  const handleSaveClick = () => {
    mutate(
      {
        currentRawProdUsages: currentRawProduct,
        desiredRawProdUsages: desiredRawProduct,
        specs: affectedRecipes,
      },
      {
        onSuccess: () => {
          setStep(4);
          showToast({ label: 'Recipes have been updated successfully.', id: 'changeRecipeRawProductsSuccess' });
        },
        onError: () => {
          showToast({
            ...genericErrorShowToastOptions,
            id: 'changeRecipeRawProductsError',
            label: 'Error updating recipes.',
          });
        },
      }
    );
  };

  return (
    <StyledReviewChanges>
      <H2>Review your changes</H2>
      <TableRowWrapper>
        <TableWrapper>
          <Table
            rowData={currentRawProduct}
            showHeader={true}
            showFooter={false}
            headerTitle='Current Raw Product'
            headerButtons={[{ label: 'Edit', variant: Variant.Text, onClick: editCurrentRawProduct }]}
            floatingFiltersHeight={0}
            columnDefs={[
              { minWidth: 110, flex: 1, field: 'moaRawProdNo', headerName: 'Raw Product' },
              { minWidth: 220, flex: 1, field: 'moaRawProdDesc', headerName: 'Description' },
              { minWidth: 110,
                 flex: 1,
                  field: 'usageAmount',
                   headerName: 'Usage Amount', 
                   resizable: false,
                   valueFormatter: (params: ValueFormatterParams) =>
                   params.value
                     ? `${params.value}`
                     : 'All',
                  
                  },
            ]}
          />
        </TableWrapper>
        <KeyboardArrowRightOutlinedIcon htmlColor={theme.colors.gray400} fontSize='large' />
        <TableWrapper>
          <Table
            rowData={desiredRawProduct}
            showHeader={true}
            showFooter={false}
            headerTitle='Desired Raw Product'
            headerButtons={[{ label: 'Edit', variant: Variant.Text, onClick: editDesiredRawProduct }]}
            floatingFiltersHeight={0}
            columnDefs={[
              { minWidth: 110, flex: 1, field: 'moaRawProdNo', headerName: 'Raw Product' },
              { minWidth: 220, flex: 1, field: 'moaRawProdDesc', headerName: 'Description' },
              { minWidth: 110, flex: 1, field: 'usageAmount', headerName: 'Usage Amount', resizable: false },
            ]}
          />
        </TableWrapper>
      </TableRowWrapper>
      <TableRowWrapper>
        <Table
          rowData={selectedRecipes}
          showHeader={true}
          showFooter={false}
          headerTitle='Affected Recipes'
          headerButtons={[{ label: 'Edit', variant: Variant.Text, onClick: editRecipes }]}
          floatingFiltersHeight={0}
          columnDefs={[
            { minWidth: 220, flex: 1, field: 'specNo', headerName: 'Recipe' },
            { minWidth: 320, flex: 2, field: 'description', headerName: 'Description', resizable: false },
          ]}
        />
      </TableRowWrapper>
      <Buttons>
        <Button label='Back' testId='reviewChangeRawProductBackButton' variant={Variant.Secondary} onClick={editRecipes} />
        <Button label='Save' testId='reviewChangeRawProductSaveButton' onClick={handleSaveClick} />
      </Buttons>
    </StyledReviewChanges>
  );
};

export default ReviewChanges;
